/**
 * Aggregate per-session `ContactSignal` rows into a single "portrait"
 * of a contact — what we've learned about them across every
 * conversation. Drives the Phase B.3 portrait block on ContactDetail.
 *
 * Aggregation rules by signal type:
 *   - currentJob / currentSchool / currentCity → latest wins; these
 *     are stateful attributes that change over time, so the most
 *     recent mention is treated as truth. Older values become
 *     historical context (not surfaced in MVP).
 *   - interest / preference / skill / belief / relationship → dedupe
 *     on a case-insensitive normalized form, count mentions, preserve
 *     the original casing of the FIRST occurrence. More mentions =
 *     stronger signal, so these sort by mentionCount desc.
 *   - lifeEvent → never dedupe; each life event is distinct. Keep
 *     full timeline so a future timeline view can render them in
 *     order.
 *
 * Pure function — no DB, no network, no side effects. Callers fetch
 * signals via `listSignalsForContact` and hand the array in.
 */
import type { ContactSignal } from "@/types/voice";

export interface LatestSignal {
  value: string;
  sourceQuote: string | null;
  sessionId: string;
  createdAt: string;
}

export interface AccumulatedSignal {
  /** Original casing of the first mention. */
  value: string;
  /** Normalized key used for dedup (lowercased, trimmed). */
  key: string;
  mentionCount: number;
  firstMentionAt: string;
  lastMentionAt: string;
  /** Every supporting quote the LM emitted, most recent first. */
  quotes: string[];
  /** Session ids this signal showed up in, deduped. */
  sessionIds: string[];
}

export interface TimelineSignal {
  value: string;
  sourceQuote: string | null;
  sessionId: string;
  createdAt: string;
}

export interface ContactPortrait {
  currentJob: LatestSignal | null;
  currentSchool: LatestSignal | null;
  currentCity: LatestSignal | null;
  interests: AccumulatedSignal[];
  preferences: AccumulatedSignal[];
  skills: AccumulatedSignal[];
  beliefs: AccumulatedSignal[];
  relationships: AccumulatedSignal[];
  lifeEvents: TimelineSignal[];
  /** Number of distinct sessions contributing to this portrait. */
  sessionCount: number;
  /** ISO timestamps of the earliest and latest signals. */
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  /** True when no signals exist at all — caller can hide the block. */
  isEmpty: boolean;
}

const STATEFUL_TYPES = new Set(["currentJob", "currentSchool", "currentCity"]);
const ACCUMULATING_TYPES = new Set([
  "interest",
  "preference",
  "skill",
  "belief",
  "relationship",
]);

export function buildContactPortrait(
  signals: ContactSignal[]
): ContactPortrait {
  const empty: ContactPortrait = {
    currentJob: null,
    currentSchool: null,
    currentCity: null,
    interests: [],
    preferences: [],
    skills: [],
    beliefs: [],
    relationships: [],
    lifeEvents: [],
    sessionCount: 0,
    firstSeenAt: null,
    lastSeenAt: null,
    isEmpty: true,
  };

  if (signals.length === 0) return empty;

  const sorted = [...signals].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );

  const statefulLatest = new Map<string, LatestSignal>();
  const accumulating = new Map<string, Map<string, AccumulatedSignal>>();
  const lifeEvents: TimelineSignal[] = [];
  const sessions = new Set<string>();

  for (const s of sorted) {
    sessions.add(s.sessionId);
    const cleanValue = s.value.trim();
    if (!cleanValue) continue;

    if (STATEFUL_TYPES.has(s.signalType)) {
      statefulLatest.set(s.signalType, {
        value: cleanValue,
        sourceQuote: s.sourceQuote,
        sessionId: s.sessionId,
        createdAt: s.createdAt,
      });
      continue;
    }

    if (ACCUMULATING_TYPES.has(s.signalType)) {
      const bucket =
        accumulating.get(s.signalType) ??
        new Map<string, AccumulatedSignal>();
      accumulating.set(s.signalType, bucket);

      const key = normalizeKey(cleanValue);
      const existing = bucket.get(key);
      if (existing) {
        existing.mentionCount += 1;
        existing.lastMentionAt = s.createdAt;
        if (s.sourceQuote && !existing.quotes.includes(s.sourceQuote)) {
          existing.quotes.unshift(s.sourceQuote);
        }
        if (!existing.sessionIds.includes(s.sessionId)) {
          existing.sessionIds.push(s.sessionId);
        }
      } else {
        bucket.set(key, {
          value: cleanValue,
          key,
          mentionCount: 1,
          firstMentionAt: s.createdAt,
          lastMentionAt: s.createdAt,
          quotes: s.sourceQuote ? [s.sourceQuote] : [],
          sessionIds: [s.sessionId],
        });
      }
      continue;
    }

    if (s.signalType === "lifeEvent") {
      lifeEvents.push({
        value: cleanValue,
        sourceQuote: s.sourceQuote,
        sessionId: s.sessionId,
        createdAt: s.createdAt,
      });
    }
  }

  lifeEvents.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return {
    currentJob: statefulLatest.get("currentJob") ?? null,
    currentSchool: statefulLatest.get("currentSchool") ?? null,
    currentCity: statefulLatest.get("currentCity") ?? null,
    interests: sortAccumulated(accumulating.get("interest")),
    preferences: sortAccumulated(accumulating.get("preference")),
    skills: sortAccumulated(accumulating.get("skill")),
    beliefs: sortAccumulated(accumulating.get("belief")),
    relationships: sortAccumulated(accumulating.get("relationship")),
    lifeEvents,
    sessionCount: sessions.size,
    firstSeenAt: sorted[0].createdAt,
    lastSeenAt: sorted[sorted.length - 1].createdAt,
    isEmpty: false,
  };
}

function normalizeKey(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/^(the|a|an)\s+/, "")
    .replace(/\s+/g, " ");
}

function sortAccumulated(
  bucket: Map<string, AccumulatedSignal> | undefined
): AccumulatedSignal[] {
  if (!bucket) return [];
  return Array.from(bucket.values()).sort((a, b) => {
    if (b.mentionCount !== a.mentionCount) {
      return b.mentionCount - a.mentionCount;
    }
    return b.lastMentionAt.localeCompare(a.lastMentionAt);
  });
}
